// Session RC1: the rate card as the office reads and edits it, shared by the subcontractor page's rate card, the Add
// subcontractor modal (Standard card read-only, Custom rates) and the Settings "Standard rate card" card.
//
// READ: every line with Bona Fide and Labour Only side by side (Labour Only through labourOnlyPence, the server's own
// arithmetic), then the uplift bands in words, never a bare percentage. EDIT: the same lines as pounds and pence, the
// uplifts as a choice of words, the evening start as a clock time, the technician block in hours.
import type { RateCardFigures, RateCardLineKey, StandardCardSettings } from "../../lib/api";
import {
  RATE_LINES,
  RC1_SETTING_DEFAULTS,
  UPLIFT_CHOICES,
  deductionText,
  hoursText,
  labourOnlyPence,
  penceText,
  upliftBands,
  upliftWords,
  type RateFiguresForm,
} from "../../lib/rateCardUi";

export type CardForDisplay = Partial<RateCardFigures> &
  Pick<StandardCardSettings, "dailyCapThresholdMins" | "saturdayMultiplierPct" | "sundayMultiplierPct" | "labourOnlyDeductionBps"> &
  Partial<Pick<StandardCardSettings, "weekdayEveningMultiplierPct" | "weekdayEveningStartMins" | "bankHolidayMultiplierPct" | "techVisitBlockMins" | "travelCapPerLegMins">>;

/** The card's lines, Bona Fide and Labour Only side by side, and the uplift bands in words. Read-only. */
export function RateCardTable({ card, caption }: { card: CardForDisplay; caption?: string }) {
  const block = card.techVisitBlockMins ?? RC1_SETTING_DEFAULTS.techVisitBlockMins;
  const unitFor = (key: RateCardLineKey, unit: string) => (key === "techVisitBlockPence" ? `per visit, up to ${hoursText(block)} on site` : unit);
  return (
    <div>
      {caption && <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">{caption}</p>}
      <table className="ele-list-table mt-2 w-full text-sm" aria-label="Rates">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-ele-body">
            <th className="py-1 pr-2" style={{ width: "50%" }}>Line</th>
            <th className="py-1 pr-2">Bona Fide</th>
            <th className="py-1 pr-2">Labour Only</th>
          </tr>
        </thead>
        <tbody>
          {RATE_LINES.map((l) => (
            <tr key={l.key} className="border-t border-ele-purple-accent/40">
              <td className="py-1 pr-2 text-ele-strong" style={{ whiteSpace: "normal" }}>
                {l.label} <span className="text-xs text-ele-body">{unitFor(l.key, l.unit)}</span>
              </td>
              <td className="py-1 pr-2 text-ele-body">{penceText(card[l.key] ?? null)}</td>
              <td className="py-1 pr-2 text-ele-body">{penceText(labourOnlyPence(card[l.key] ?? null, card.labourOnlyDeductionBps))}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-ele-body">{deductionText(card.labourOnlyDeductionBps)}</p>
      <UpliftBandsList card={card} />
    </div>
  );
}

export function UpliftBandsList({ card }: { card: CardForDisplay }) {
  return (
    <div className="mt-3">
      <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">Call-out and repair labour, by when it is done</p>
      {/* One column on a phone: the label sits above its figure. Two columns (label | figure) squeezed "Weekdays before 18:00" past its
          box at 360px and 1.5x text, where the figure's auto column took the width (Session DOC1-TIDY). Two columns again from sm. */}
      <dl className="mt-1 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-[minmax(0,1fr)_auto]" aria-label="Uplift bands">
        {upliftBands(card).map((b) => (
          <div key={b.when} className="contents">
            <dt className="text-ele-body">{b.when}</dt>
            <dd className="mb-1 text-ele-strong sm:mb-0">{b.words}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-1 text-xs text-ele-body">
        The day cap covers normal-rate call-out and repair hours in the first {hoursText(card.dailyCapThresholdMins)} of a day; uplifted hours are paid on top. Travel, flat
        visits, the technician block and modernisation days are never uplifted.
        {card.travelCapPerLegMins != null ? ` Travel is one way, capped at ${card.travelCapPerLegMins} minutes a leg.` : " Travel is one way, for the journey to site."}
      </p>
    </div>
  );
}

const INPUT = "w-full rounded-ele border border-ele-field-border bg-ele-field px-2 py-1 text-sm text-ele-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ele-green";

export function UpliftSelect({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const n = Number(value);
  const extra = Number.isInteger(n) && !(UPLIFT_CHOICES as readonly number[]).includes(n) ? n : null;
  return (
    <label className="text-sm text-ele-body">
      {label}
      <select className={INPUT} value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        {UPLIFT_CHOICES.map((p) => (
          <option key={p} value={String(p)}>
            {upliftWords(p)}
          </option>
        ))}
        {extra != null && <option value={String(extra)}>{upliftWords(extra)}</option>}
      </select>
    </label>
  );
}

/** The figures and settings of a card as form fields. Controlled: the parent holds the RateFiguresForm. */
export function RateFiguresFields({ form, onChange }: { form: RateFiguresForm; onChange: (next: RateFiguresForm) => void }) {
  const setFigure = (key: RateCardLineKey, v: string) => onChange({ ...form, figures: { ...form.figures, [key]: v } });
  const set = <K extends Exclude<keyof RateFiguresForm, "figures">>(k: K, v: string) => onChange({ ...form, [k]: v });
  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-2">
        {RATE_LINES.map((l) => (
          <label key={l.key} className="text-sm text-ele-body">
            {l.label} <span className="text-xs">({l.unit})</span>
            <input
              type="text"
              inputMode="decimal"
              aria-label={l.label}
              className={INPUT}
              value={form.figures[l.key]}
              placeholder="Not agreed"
              onChange={(e) => setFigure(l.key, e.target.value)}
            />
          </label>
        ))}
      </div>
      <p className="mt-4 font-menu text-xs uppercase tracking-widest text-ele-blue">Uplifts on call-out and repair labour</p>
      <div className="mt-1 grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-ele-body">
          Weekday evening starts at
          <input type="time" aria-label="Weekday evening starts at" className={INPUT} value={form.weekdayEveningStart} onChange={(e) => set("weekdayEveningStart", e.target.value)} />
        </label>
        <UpliftSelect label="Weekdays from then" value={form.weekdayEveningMultiplierPct} onChange={(v) => set("weekdayEveningMultiplierPct", v)} />
        <UpliftSelect label="Saturday" value={form.saturdayMultiplierPct} onChange={(v) => set("saturdayMultiplierPct", v)} />
        <UpliftSelect label="Sunday" value={form.sundayMultiplierPct} onChange={(v) => set("sundayMultiplierPct", v)} />
        <UpliftSelect label="Bank holidays" value={form.bankHolidayMultiplierPct} onChange={(v) => set("bankHolidayMultiplierPct", v)} />
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-ele-body">
          Day cap covers the first (minutes)
          <input type="text" inputMode="numeric" aria-label="Day cap covers the first (minutes)" className={INPUT} value={form.dailyCapThresholdMins} onChange={(e) => set("dailyCapThresholdMins", e.target.value)} />
        </label>
        <label className="text-sm text-ele-body">
          Technician block covers (hours on site)
          <input type="text" inputMode="decimal" aria-label="Technician block covers (hours on site)" className={INPUT} value={form.techVisitBlockMins} onChange={(e) => set("techVisitBlockMins", e.target.value)} />
        </label>
        <label className="text-sm text-ele-body">
          Travel cap per leg (minutes)
          <input type="text" inputMode="numeric" aria-label="Travel cap per leg (minutes)" className={INPUT} value={form.travelCapPerLegMins} placeholder="No cap" onChange={(e) => set("travelCapPerLegMins", e.target.value)} />
        </label>
        <label className="text-sm text-ele-body">
          Labour Only deduction (percent)
          <input type="text" inputMode="decimal" aria-label="Labour Only deduction (percent)" className={INPUT} value={form.labourOnlyDeductionPct} placeholder="Not set" onChange={(e) => set("labourOnlyDeductionPct", e.target.value)} />
        </label>
      </div>
    </div>
  );
}
